import React, { useState, useEffect } from 'react';
import { PasslyAutoComplete, PasslyDropzoneAreaComponent } from '@app/componentsV2';
import { useTypedTranslation } from '@app/hooks';
import { useSelector } from 'react-redux';
import { localStore, genericErrorToast, useDebounce, searchMinimumLength, displayName, active, defaultOrgId, IndividualVaultType } from '@app/helpers';
import { Typography, Grid } from '@material-ui/core';
import { vaultsService, vaultFoldersService } from '@app/services';
import { Order, IVaultsDisplay, IVaultFolder, Organization } from '@app/models';
import { PasslySelect } from '@app/components';

interface IReceivedData {
    currentVault: string;
    currentVaultObject: IVaultsDisplay;
    currentFolder: string;
    currentAvailableVaults: IVaultsDisplay[];
    currentAvailableFolders: IVaultFolder[];
    currentFile: any;
    returnAllAvailableVaults: Function;
    returnAllAvailableFolders: Function;
    returnFile: Function;
    returnOrg: Function;
    returnVault: Function;
    returnFolder: Function;
}

const UploadPasswordImportZipComponent = (props: IReceivedData) => {
    const { translation } = useTypedTranslation();

    const localStorageOrganization: string = localStore.getItem('org_filter') !== null ? localStore.getItem('org_filter')! : '';
    const currentUserId = useSelector((state: any) => state.signIn.currentUser.userUuid);

    const allOrgs: Organization[] = useSelector((state: any) => state.basic.shellConfig.organizations);
    const [availableOrgs, setAvailableOrgs] = useState<Organization[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<string>(localStorageOrganization);

    const [availableVaults, setAvailableVaults] = useState<IVaultsDisplay[]>(props.currentAvailableVaults);
    const [selectedVault, setSelectedVault] = useState<string>(props.currentVault);
    const [selectedVaultObject, setSelectedVaultObject] = useState<any>(props.currentVaultObject);

    const [folders, setFolders] = useState<IVaultFolder[]>(props.currentAvailableFolders);
    const [selectedFolder, setSelectedFolder] = useState<string>(props.currentFolder);

    const [vaultFilterText, setVaultFilterText] = useState<string>('');
    const debouncedSearch = useDebounce(vaultFilterText, 500);

    useEffect(() => {
        // only show orgs that have access to pws
        const orgsWithAccess: Organization[] = allOrgs.filter(function (org: Organization) {
            return org.provisioningState === active && (org.license.passwordServerEnabled === true || org.id === defaultOrgId);
        });

        setAvailableOrgs(orgsWithAccess);
        props.returnOrg(selectedOrg);
    }, []);

    useEffect(() => {
        if (debouncedSearch === '' && props.currentVault === '') {
            getVaults(selectedOrg);
        } else if (debouncedSearch.length >= searchMinimumLength) {
            getVaults(selectedOrg);
        }
    }, [debouncedSearch]);

    const getVaults = (org: string) => {
        vaultsService.getAllModifiableVaults(Order.asc, org).then(handleSuccessVault).catch(genericErrorToast);
    };

    const handleSuccessVault = (response: any) => {
        const results: IVaultsDisplay[] = response;

        const notOtherPeoplesIndividualVaults: IVaultsDisplay[] = results?.filter(function (row: IVaultsDisplay) {
            return !(row.VaultType === IndividualVaultType && row.OwnerId !== currentUserId);
        });

        setAvailableVaults(notOtherPeoplesIndividualVaults);
        props.returnAllAvailableVaults(notOtherPeoplesIndividualVaults);

        if (notOtherPeoplesIndividualVaults.length > 0) {
            setSelectedVault(notOtherPeoplesIndividualVaults[0].Id);
            setSelectedVaultObject(notOtherPeoplesIndividualVaults[0]);
            props.returnVault(notOtherPeoplesIndividualVaults[0].Id);
            getFolders(notOtherPeoplesIndividualVaults[0].Id);
        }
    };

    const handleOrgChange = () => (event: React.ChangeEvent<HTMLInputElement>) => {
        const chosen: string = event.target.value as string;

        localStore.setItem('org_filter', chosen);
        setSelectedOrg(chosen);
        props.returnOrg(chosen);

        setSelectedVault('');
        setSelectedFolder('');

        getVaults(chosen);
    };

    const handleVaultChange = (e: any) => {
        const chosen = e.target.value;

        if (chosen && chosen.Id !== '') {
            setSelectedVault(chosen.Id);
            setSelectedVaultObject(chosen);
            props.returnVault(chosen.Id, chosen);

            setSelectedFolder('');
            getFolders(chosen.Id);
        } else {
            setSelectedVault('');
            setSelectedVaultObject({});
            props.returnVault('');

            setSelectedFolder('');
        }
    };

    const handleFolderChange = () => (event: React.ChangeEvent<HTMLInputElement>) => {
        const chosen: string = event.target.value as string;

        setSelectedFolder(chosen);
        props.returnFolder(chosen);
    };

    const getFolders = (vaultId: string) => {
        vaultFoldersService.getAllVaultFolders(vaultId, Order.asc).then(handleFolderSuccess);
    };

    const handleFolderSuccess = (response: any) => {
        const results: IVaultFolder[] = response;

        setFolders(results);
        props.returnAllAvailableFolders(results);

        // default to default folder
        if (results.length > 0) {
            const defaultFolder: IVaultFolder[] = results.filter(function (folder: IVaultFolder) {
                return folder.IsDefault === true;
            });

            if (defaultFolder.length > 0) {
                setSelectedFolder(defaultFolder[0].Id);
                props.returnFolder(defaultFolder[0].Id);
            }
        }
    };

    const handleFileChange = (fileUploaded: any) => {
        props.returnFile(fileUploaded);
    };

    return (
        <>
            <div className='passwordImportChoice'>
                <div className='passwordImportChoice__gettingStarted'>
                    <div>
                        <Typography variant='subtitle1' color='primary'>
                            {translation('IMPORTER_UPLOAD_INSTRUCTIONS')}
                        </Typography>
                        <PasslyDropzoneAreaComponent handleChange={handleFileChange} />
                    </div>
                    <br />
                    <Grid container>
                        <PasslySelect
                            id='orgDropdown'
                            label={'ORGANIZATION'}
                            value={selectedOrg}
                            onChange={handleOrgChange()}
                            options={availableOrgs}
                            optionLabel='name'
                            optionValue='id'
                        />
                    </Grid>

                    <Grid container>
                        <PasslyAutoComplete
                            className='passwordImportChoice__autocomplete'
                            items={availableVaults}
                            onChange={handleVaultChange}
                            id='vaultSelect'
                            label={'VAULTS'}
                            value={selectedVault}
                            valueField='Id'
                            field='DisplayName'
                            dropdown={true}
                        />
                    </Grid>

                    <Grid container>
                        <PasslySelect
                            id=''
                            label={'VAULT_FOLDER'}
                            value={selectedFolder}
                            onChange={handleFolderChange()}
                            options={folders}
                            optionLabel='DisplayName'
                            optionValue='Id'
                        />
                    </Grid>
                </div>
            </div>
        </>
    );
};

export default UploadPasswordImportZipComponent;